import React, { useEffect, useState } from 'react';
import { Achievement, AchievementDefinition } from '../types/mundus';
import { getAvailableAchievements, getMundusAchievements } from '../lib/achievements.api';
import AchievementCard from './AchievementCard';

interface AchievementGalleryProps {
  mundusId: string;
}

type FilterOption = 'all' | 'unlocked' | 'locked';
type SortOption = 'recent' | 'points' | 'tier';

export const AchievementGallery: React.FC<AchievementGalleryProps> = ({ mundusId }) => {
  const [availableAchievements, setAvailableAchievements] = useState<AchievementDefinition[]>([]);
  const [unlockedAchievements, setUnlockedAchievements] = useState<Achievement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<FilterOption>('all');
  const [sortBy, setSortBy] = useState<SortOption>('recent');

  useEffect(() => {
    loadAchievements();
  }, [mundusId]);

  const loadAchievements = async () => {
    try {
      setIsLoading(true);
      const [available, unlocked] = await Promise.all([
        getAvailableAchievements(),
        getMundusAchievements(mundusId),
      ]);
      setAvailableAchievements(available);
      setUnlockedAchievements(unlocked);
    } catch (error) {
      console.error('Error loading achievements:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDisplayAchievements = () => {
    const unlockedIds = new Set(unlockedAchievements.map((a) => a.achievementId));

    let items: Array<{ achievement: AchievementDefinition | Achievement; isUnlocked: boolean }> = [];

    if (filter === 'all' || filter === 'unlocked') {
      items = [
        ...items,
        ...unlockedAchievements.map((a) => ({ achievement: a, isUnlocked: true })),
      ];
    }

    if (filter === 'all' || filter === 'locked') {
      const locked = availableAchievements
        .filter((a) => !unlockedIds.has(a.id))
        .map((a) => ({ achievement: a, isUnlocked: false }));
      items = [...items, ...locked];
    }

    // Sort
    if (sortBy === 'recent') {
      items.sort((a, b) => {
        if (!a.isUnlocked && !b.isUnlocked) return 0;
        if (!a.isUnlocked) return 1;
        if (!b.isUnlocked) return -1;
        const dateA = 'unlockedAt' in a.achievement ? new Date(a.achievement.unlockedAt).getTime() : 0;
        const dateB = 'unlockedAt' in b.achievement ? new Date(b.achievement.unlockedAt).getTime() : 0;
        return dateB - dateA;
      });
    } else if (sortBy === 'points') {
      items.sort((a, b) => b.achievement.points - a.achievement.points);
    } else if (sortBy === 'tier') {
      const tierOrder = { platinum: 0, gold: 1, silver: 2, bronze: 3 };
      items.sort((a, b) => {
        const tierA = tierOrder[a.achievement.tier as keyof typeof tierOrder];
        const tierB = tierOrder[b.achievement.tier as keyof typeof tierOrder];
        return tierA - tierB;
      });
    }

    return items;
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  const displayItems = getDisplayAchievements();

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Achievements</h2>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">
            {unlockedAchievements.length} / {availableAchievements.length}
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        {/* Filter by status */}
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Show:</span>
          <div className="flex space-x-1">
            {(['all', 'unlocked', 'locked'] as FilterOption[]).map((option) => (
              <button
                key={option}
                onClick={() => setFilter(option)}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  filter === option
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Sort by */}
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Sort:</span>
          <div className="flex space-x-1">
            {(['recent', 'points', 'tier'] as SortOption[]).map((option) => (
              <button
                key={option}
                onClick={() => setSortBy(option)}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  sortBy === option
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Achievement Grid */}
      {displayItems.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {displayItems.map((item) => (
            <AchievementCard
              key={'id' in item.achievement ? item.achievement.id : item.achievement.achievementId}
              achievement={item.achievement}
              isUnlocked={item.isUnlocked}
              size="medium"
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-500">No achievements to display</p>
        </div>
      )}
    </div>
  );
};

export default AchievementGallery;
